/** @jsxImportSource @emotion/react */
import React from "react";
import {
  Alert,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Button,
  Select,
  InputNumber,
} from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Styles from "./styles";
import { HomeUsecase, useHomeDispatch } from "../../../usecases/Home";
import { RequestHomeDto } from "../../../services/Home/dto/request.dto";
import { homeFormSchema } from "./formSchema";

interface ModalRequestHomeProps {
  onCancel: () => void;
  onSuccess: () => void;
  visible: boolean;
}

const ModalRequestHomeComponent: React.FC<ModalRequestHomeProps> = ({
  visible,
  onCancel,
  onSuccess,
}: ModalRequestHomeProps) => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<RequestHomeDto>({
    mode: "onSubmit",
    resolver: yupResolver(homeFormSchema),
  });
  const homeDispatch = useHomeDispatch();

  const handleSubmitHandle = React.useCallback(
    async (data: RequestHomeDto) => {
      setIsProcessing(true);
      try {
        if (homeDispatch) {
          const homeUsecase = new HomeUsecase(homeDispatch);
          await homeUsecase.request(data);
        }
        reset();
        setErrorMessage("");
        onSuccess();
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
      } finally {
        setIsProcessing(false);
      }
    },
    [homeDispatch, reset, onSuccess],
  );

  const onCancelHandle = React.useCallback(() => {
    reset();
    setErrorMessage("");
    onCancel();
  }, [onCancel, reset]);

  return (
    <Modal
      title="Request New Home"
      visible={visible}
      closable={false}
      footer={[
        <Button key="back" size="large" onClick={onCancelHandle}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          loading={isProcessing}
          onClick={handleSubmit(handleSubmitHandle)}
        >
          Request
        </Button>,
      ]}
    >
      <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
        {errorMessage && (
          <Row justify="center">
            <Col css={Styles.colGutterStyle}>
              <Alert message={errorMessage} type="error" />
            </Col>
          </Row>
        )}
        <Form.Item
          label="Name"
          css={Styles.labelStyle}
          validateStatus={errors.homeName ? "error" : "validating"}
          help={errors.homeName?.message}
        >
          <Controller
            name="homeName"
            control={control}
            render={({ field }) => <Input {...field} size="large" />}
          />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              label="Range"
              css={Styles.labelStyle}
              validateStatus={errors.homeRange ? "error" : "validating"}
              help={errors.homeRange?.message}
            >
              <Controller
                name="homeRange"
                control={control}
                render={({ field }) => (
                  <Select {...field} size="large">
                    <Select.Option value="range1">Range 1</Select.Option>
                    <Select.Option value="range2">Range 2</Select.Option>
                  </Select>
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              label="Storey"
              css={Styles.labelStyle}
              validateStatus={errors.homeStorey ? "error" : "validating"}
              help={errors.homeStorey?.message}
            >
              <Controller
                name="homeStorey"
                control={control}
                render={({ field }) => (
                  <Select {...field} size="large">
                    <Select.Option value="single">Single</Select.Option>
                    <Select.Option value="double">Double</Select.Option>
                  </Select>
                )}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={8}>
            <Form.Item
              label="Number of Bed"
              css={Styles.labelStyle}
              validateStatus={errors.homeBedNo ? "error" : "validating"}
              help={errors.homeBedNo?.message}
            >
              <Controller
                name="homeBedNo"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="Number of Bath"
              css={Styles.labelStyle}
              validateStatus={errors.homeBathNo ? "error" : "validating"}
              help={errors.homeBathNo?.message}
            >
              <Controller
                name="homeBathNo"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="Number of Garage"
              css={Styles.labelStyle}
              validateStatus={errors.homeGarageNo ? "error" : "validating"}
              help={errors.homeGarageNo?.message}
            >
              <Controller
                name="homeGarageNo"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={8}>
            <Form.Item
              label="From Price"
              css={Styles.labelStyle}
              validateStatus={errors.homeFromPrice ? "error" : "validating"}
              help={errors.homeFromPrice?.message}
            >
              <Controller
                name="homeFromPrice"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="To Price"
              css={Styles.labelStyle}
              validateStatus={errors.homeToPrice ? "error" : "validating"}
              help={errors.homeToPrice?.message}
            >
              <Controller
                name="homeToPrice"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="Lot Size"
              css={Styles.labelStyle}
              validateStatus={errors.homeLotSize ? "error" : "validating"}
              help={errors.homeLotSize?.message}
            >
              <Controller
                name="homeLotSize"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export const ModalRequestHome = React.memo(ModalRequestHomeComponent);
